

import React, { useState, useMemo, useEffect } from 'react';
// FIX: Corrected Firebase User type usage.
import firebase from 'firebase/compat/app';
import { Transaction } from '../types';
import { EditIcon, DeleteIcon } from './icons';
import { formatCurrency } from '../utils/formatting';
import ConfirmationModal from './ConfirmationModal';
import ReauthDeleteModal from './ReauthDeleteModal';

interface Column {
    key: string;
    name: string;
}

interface DataViewProps {
    title: string;
    description: string;
    columns: Column[];
    data: Transaction[];
    onEdit: (transaction: Transaction) => void;
    onDelete: (id: string) => void;
    categories: string[];
    initialFilter?: 'all' | 'income' | 'expense' | null;
    clearInitialFilter?: () => void;
    currency: string;
    user: firebase.User | null;
    onBulkUpdate: (ids: string[], data: Partial<Omit<Transaction, 'id' | 'balance'>>) => void;
    onBulkDelete: (ids: string[]) => void;
}

const DataView: React.FC<DataViewProps> = ({ title, description, columns, data, onEdit, onDelete, categories, initialFilter, clearInitialFilter, currency, user, onBulkUpdate, onBulkDelete }) => {
    
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [transactionType, setTransactionType] = useState<'all' | 'income' | 'expense'>('all');
    
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [modalState, setModalState] = useState<{ type: 'category' | 'payment' | 'delete' | null; isOpen: boolean }>({ type: null, isOpen: false });
    const [bulkCategory, setBulkCategory] = useState('');
    const [bulkPaymentMethod, setBulkPaymentMethod] = useState('');
    const [deletingTransaction, setDeletingTransaction] = useState<Transaction | null>(null);

    useEffect(() => {
        if (initialFilter) {
            setTransactionType(initialFilter);
            if (clearInitialFilter) {
                clearInitialFilter();
            }
        }
    }, [initialFilter, clearInitialFilter]);
    
    const filteredData = useMemo(() => {
        return data.filter(transaction => {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();

            const matchesSearch = searchTerm === '' ||
                transaction.concept.toLowerCase().includes(lowerCaseSearchTerm) ||
                transaction.counterparty.toLowerCase().includes(lowerCaseSearchTerm);

            const matchesCategory = selectedCategory === '' ||
                (transaction.category && transaction.category.includes(selectedCategory));
            
            if (!transaction.date) return false;

            const transactionDate = new Date(transaction.date);
            const matchesStartDate = startDate === '' || transactionDate >= new Date(new Date(startDate).setHours(0, 0, 0, 0));
            const matchesEndDate = endDate === '' || transactionDate <= new Date(new Date(endDate).setHours(23, 59, 59, 999));

            const matchesType = transactionType === 'all' ||
                (transactionType === 'income' && transaction.amount > 0) ||
                (transactionType === 'expense' && transaction.amount < 0);

            return matchesSearch && matchesCategory && matchesStartDate && matchesEndDate && matchesType;
        });
    }, [data, searchTerm, selectedCategory, startDate, endDate, transactionType]);

    // Deselect all when filters change to avoid confusion
    useEffect(() => {
        setSelectedIds([]);
    }, [searchTerm, startDate, endDate, selectedCategory, transactionType]);


    const totals = useMemo(() => {
        const income = filteredData.reduce((acc, t) => (t.amount > 0 ? acc + t.amount : acc), 0);
        const expense = filteredData.reduce((acc, t) => (t.amount < 0 ? acc + t.amount : acc), 0);
        const net = income + expense;
        return { income, expense, net };
    }, [filteredData]);
    
    const clearFilters = () => {
        setSearchTerm('');
        setStartDate('');
        setEndDate('');
        setSelectedCategory('');
        setTransactionType('all');
    };
    
    const handleSelect = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedIds(filteredData.map(t => t.id));
        } else {
            setSelectedIds([]);
        }
    };
    
    const handleConfirmBulkAction = async () => {
        if (modalState.type === 'category' && bulkCategory) {
            await onBulkUpdate(selectedIds, { category: [bulkCategory] });
        }
        if (modalState.type === 'payment' && bulkPaymentMethod) {
            await onBulkUpdate(selectedIds, { paymentMethod: bulkPaymentMethod });
        }
        setModalState({ type: null, isOpen: false });
        setSelectedIds([]); // Clear selection after action
        setBulkCategory('');
        setBulkPaymentMethod('');
    };

    const handleConfirmBulkDelete = async () => {
        await onBulkDelete(selectedIds);
        setModalState({ type: null, isOpen: false });
        setSelectedIds([]);
    };

    const handleConfirmSingleDelete = () => {
        if (deletingTransaction) {
            onDelete(deletingTransaction.id);
            setDeletingTransaction(null);
        }
    };

    return (
        <>
            <div className="bg-light-surface dark:bg-surface p-4 sm:p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary">{title}</h3>
                        <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-1">{description}</p>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-light-border dark:border-border">
                    <div className="lg:col-span-1">
                        <label htmlFor="search-term" className="text-xs font-medium text-light-text-secondary dark:text-text-secondary">Buscar</label>
                        <input
                            id="search-term"
                            type="text"
                            placeholder="Concepto o contrapartida..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full mt-1 bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                     <div className="lg:col-span-1">
                        <label htmlFor="transaction-type" className="text-xs font-medium text-light-text-secondary dark:text-text-secondary">Tipo de movimiento</label>
                        <div className="flex gap-1 rounded-lg bg-gray-200 dark:bg-gray-900 p-1 mt-1">
                            {(['all', 'income', 'expense'] as const).map(option => (
                                <button key={option} type="button" onClick={() => setTransactionType(option)} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${transactionType === option ? 'bg-primary text-white shadow' : 'text-light-text-secondary dark:text-text-secondary hover:bg-gray-300 dark:hover:bg-gray-700'}`}>
                                    {option === 'all' ? 'Todos' : option === 'income' ? 'Ingresos' : 'Gastos'}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-1">
                        <label htmlFor="category-filter" className="text-xs font-medium text-light-text-secondary dark:text-text-secondary">Categoría</label>
                        <select
                            id="category-filter"
                            value={selectedCategory}
                            onChange={e => setSelectedCategory(e.target.value)}
                            className="w-full mt-1 bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <option value="">Todas las categorías</option>
                            {categories.sort().map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                    </div>
                    <div className="lg:col-span-2">
                        <label className="text-xs font-medium text-light-text-secondary dark:text-text-secondary">Rango de Fechas</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary date-picker-styling"/>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary date-picker-styling"/>
                        </div>
                    </div>
                    <div className="flex items-end lg:col-span-1">
                        <button onClick={clearFilters} className="w-full px-3 py-2 text-sm font-medium bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 rounded-md transition-colors text-light-text-primary dark:text-text-primary">
                            Limpiar
                        </button>
                    </div>
                </div>

                 {/* Bulk Action Bar */}
                {selectedIds.length > 0 && (
                    <div className="bg-primary/10 dark:bg-primary/20 p-4 rounded-lg border border-primary/30 mb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex-shrink-0">
                            <strong className="text-primary font-bold">{selectedIds.length}</strong>
                            <span className="text-light-text-primary dark:text-text-primary ml-2">seleccionados</span>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                            <div className="flex items-center gap-2">
                                <select 
                                    value={bulkCategory}
                                    onChange={e => setBulkCategory(e.target.value)}
                                    className="bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value="" disabled>Cambiar categoría...</option>
                                    {categories.sort().map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                                <button onClick={() => setModalState({ type: 'category', isOpen: true })} disabled={!bulkCategory} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-md hover:bg-blue-600 disabled:bg-gray-500">Aplicar</button>
                            </div>
                            <div className="flex items-center gap-2">
                                <select 
                                     value={bulkPaymentMethod}
                                     onChange={e => setBulkPaymentMethod(e.target.value)}
                                     className="bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value="" disabled>Fijar método pago...</option>
                                    {["Efectivo", "Tarjeta", "Transferencia", "Bizum", "Recibo Bancario", "Otro"].map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                                <button onClick={() => setModalState({ type: 'payment', isOpen: true })} disabled={!bulkPaymentMethod} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-md hover:bg-blue-600 disabled:bg-gray-500">Aplicar</button>
                            </div>
                            <button onClick={() => setModalState({ type: 'delete', isOpen: true })} className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-md hover:bg-red-700">
                                <DeleteIcon className="w-4 h-4" /> Eliminar
                            </button>
                        </div>
                    </div>
                )}


                {/* Mobile Card View */}
                <div className="md:hidden">
                    {filteredData.length > 0 && (
                        <div className="flex items-center px-1 pb-4">
                            <input
                                type="checkbox"
                                id="select-all-mobile"
                                onChange={handleSelectAll}
                                checked={filteredData.length > 0 && selectedIds.length === filteredData.length}
                                className="custom-checkbox h-5 w-5 rounded border-gray-400 bg-white dark:bg-gray-700 text-primary focus:ring-primary cursor-pointer mr-3"
                                aria-label="Seleccionar todo"
                            />
                            <label htmlFor="select-all-mobile" className="cursor-pointer text-sm font-medium text-light-text-secondary dark:text-text-secondary">
                                Seleccionar todo
                            </label>
                        </div>
                    )}
                    <div className="space-y-4">
                        {filteredData.map((row) => (
                            <div key={row.id} className={`p-4 rounded-lg border space-y-3 transition-colors ${selectedIds.includes(row.id) ? 'bg-primary/10 border-primary' : 'bg-white dark:bg-gray-800 border-light-border dark:border-border'}`}>
                                <div className="flex justify-between items-start">
                                    <div className="flex items-start">
                                        <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} className="custom-checkbox h-5 w-5 rounded border-gray-400 bg-white dark:bg-gray-700 text-primary focus:ring-primary mr-4 mt-1 cursor-pointer"/>
                                        <div>
                                            <p className="text-xs text-light-text-secondary dark:text-text-secondary">{row.date}</p>
                                            <p className="font-semibold text-light-text-primary dark:text-text-primary mt-1">{row.concept}</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{row.counterparty}</p>
                                        </div>
                                    </div>
                                    <span className={`font-bold text-lg whitespace-nowrap ${row.amount > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                        {formatCurrency(row.amount, currency)}
                                    </span>
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-2">
                                    {Array.isArray(row.category) && row.category.length > 0
                                        ? row.category.map((cat: string) => <span key={cat} className={`${cat === 'Desconocida' ? 'bg-yellow-500/20 text-yellow-500 dark:text-yellow-300' : 'bg-gray-200 dark:bg-gray-700 text-light-text-secondary dark:text-text-secondary'} text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full`}>{cat}</span>)
                                        : <span className="text-gray-500 italic text-xs">Sin categoría</span>
                                    }
                                </div>
                                {row.paymentMethod && (
                                    <p className="text-xs text-light-text-secondary dark:text-text-secondary">
                                        Método: <span className="font-semibold text-gray-700 dark:text-gray-300">{row.paymentMethod}</span>
                                    </p>
                                )}
                                
                                <div className="flex justify-end gap-2 border-t border-light-border dark:border-gray-700 pt-3 mt-3">
                                    <button onClick={() => onEdit(row)} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md text-light-text-primary dark:text-text-primary" aria-label="Editar transacción" title="Editar">
                                        <EditIcon className="w-4 h-4"/> Editar
                                    </button>
                                    <button onClick={() => setDeletingTransaction(row)} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 dark:bg-red-800/80 hover:bg-red-700 rounded-md text-white" aria-label="Eliminar transacción" title="Eliminar">
                                        <DeleteIcon className="w-4 h-4"/> Eliminar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {filteredData.length > 0 && (
                    <div className="md:hidden mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg space-y-2 border border-light-border dark:border-border">
                        <div className="flex justify-between font-semibold">
                            <span className="text-light-text-primary dark:text-text-primary">Total Ingresos:</span>
                            <span className="text-green-500">{formatCurrency(totals.income, currency)}</span>
                        </div>
                        <div className="flex justify-between font-semibold">
                            <span className="text-light-text-primary dark:text-text-primary">Total Gastos:</span>
                            <span className="text-red-500">{formatCurrency(totals.expense, currency)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg border-t border-light-border dark:border-gray-700 pt-2 mt-2">
                            <span className="text-light-text-primary dark:text-text-primary">Total Neto:</span>
                            <span className={totals.net >= 0 ? 'text-green-500' : 'text-red-500'}>{formatCurrency(totals.net, currency)}</span>
                        </div>
                    </div>
                )}

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm text-left text-light-text-secondary dark:text-gray-400">
                        <thead className="text-xs text-light-text-secondary dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                            <tr>
                                <th scope="col" className="px-4 py-3 w-12">
                                    <input type="checkbox" onChange={handleSelectAll} checked={filteredData.length > 0 && selectedIds.length === filteredData.length} className="custom-checkbox h-4 w-4 rounded border-gray-400 bg-white dark:bg-gray-700 text-primary focus:ring-primary cursor-pointer"/>
                                </th>
                                {columns.map(col => (
                                    <th key={col.key} scope="col" className="px-6 py-3">{col.name}</th>
                                ))}
                                <th scope="col" className="px-6 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((row) => (
                                <tr key={row.id} className={`border-b border-light-border dark:border-border hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${selectedIds.includes(row.id) ? 'bg-primary/10' : 'bg-light-surface dark:bg-surface'}`}>
                                    <td className="px-4 py-4">
                                        <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} className="custom-checkbox h-4 w-4 rounded border-gray-400 bg-white dark:bg-gray-700 text-primary focus:ring-primary cursor-pointer"/>
                                    </td>
                                    {columns.map(col => (
                                        <td key={`${col.key}-${row.id}`} className="px-6 py-4 whitespace-nowrap">
                                            {col.key === 'amount' ? 
                                                <span className={`font-mono ${row.amount > 0 ? 'text-green-500 font-medium' : 'text-red-500'}`}>{formatCurrency(row.amount, currency)}</span> :
                                            col.key === 'balance' ?
                                                <span className="text-light-text-secondary dark:text-text-secondary font-mono">{formatCurrency(row.balance, currency)}</span> :
                                            col.key === 'category' ? 
                                                (Array.isArray(row.category) && row.category.length > 0
                                                    ? row.category.map((cat: string) => <span key={cat} className={`${cat === 'Desconocida' ? 'bg-yellow-500/20 text-yellow-500 dark:text-yellow-300' : 'bg-gray-200 dark:bg-gray-700 text-light-text-secondary dark:text-text-secondary'} text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full`}>{cat}</span>) 
                                                    : <span className="text-gray-500 italic">Sin categoría</span>)
                                            : (row[col.key as keyof Transaction] as string) || <span className="text-gray-500 italic">N/A</span>
                                            }
                                        </td>
                                    ))}
                                    <td className="px-6 py-4 text-right">
                                        <button onClick={() => onEdit(row)} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-primary transition-colors" aria-label="Editar transacción" title="Editar">
                                            <EditIcon className="w-5 h-5"/>
                                        </button>
                                        <button onClick={() => setDeletingTransaction(row)} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-red-500 transition-colors" aria-label="Eliminar transacción" title="Eliminar">
                                            <DeleteIcon className="w-5 h-5"/>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                         {filteredData.length > 0 && (
                            <tfoot>
                                <tr className="font-semibold bg-gray-100 dark:bg-gray-700 text-light-text-primary dark:text-text-primary">
                                    <td colSpan={4} className="px-6 py-2 text-right border-t-2 border-light-border dark:border-gray-600">Total Ingresos:</td>
                                    <td className="px-6 py-2 text-green-500 font-medium border-t-2 border-light-border dark:border-gray-600 font-mono">
                                        {formatCurrency(totals.income, currency)}
                                    </td>
                                    <td colSpan={3} className="border-t-2 border-light-border dark:border-gray-600"></td>
                                </tr>
                                <tr className="font-semibold bg-gray-100 dark:bg-gray-700 text-light-text-primary dark:text-text-primary">
                                    <td colSpan={4} className="px-6 py-2 text-right">Total Gastos:</td>
                                    <td className="px-6 py-2 text-red-500 font-mono">
                                        {formatCurrency(totals.expense, currency)}
                                    </td>
                                    <td colSpan={3}></td>
                                </tr>
                                <tr className="font-bold bg-gray-200 dark:bg-gray-800 text-light-text-primary dark:text-text-primary text-base">
                                    <td colSpan={4} className="px-6 py-3 text-right border-t-2 border-light-border dark:border-border">Total Neto (Búsqueda):</td>
                                    <td className={`px-6 py-3 font-mono ${totals.net >= 0 ? 'text-green-500' : 'text-red-500'} border-t-2 border-light-border dark:border-border`}>
                                        {formatCurrency(totals.net, currency)}
                                    </td>
                                    <td colSpan={3} className="border-t-2 border-light-border dark:border-border"></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                {filteredData.length === 0 && <p className="text-center py-8 text-light-text-secondary dark:text-text-secondary">No hay transacciones que coincidan con tu búsqueda.</p>}
            </div>
            
            <ConfirmationModal
                isOpen={!!deletingTransaction}
                onClose={() => setDeletingTransaction(null)}
                onConfirm={handleConfirmSingleDelete}
                title="Confirmar Eliminación"
                message={`¿Estás seguro de que quieres eliminar la transacción "${deletingTransaction?.concept}"? Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
            />
            
            <ConfirmationModal
                isOpen={modalState.isOpen && (modalState.type === 'category' || modalState.type === 'payment')}
                onClose={() => setModalState({ type: null, isOpen: false })}
                onConfirm={handleConfirmBulkAction}
                title="Confirmar Acción en Bloque"
                message={`Estás a punto de modificar ${selectedIds.length} transacciones con los nuevos datos. ¿Deseas continuar?`}
                confirmText="Confirmar"
            />

            <ReauthDeleteModal
                isOpen={modalState.isOpen && modalState.type === 'delete'}
                onClose={() => setModalState({ type: null, isOpen: false })}
                onConfirm={handleConfirmBulkDelete}
                user={user}
                itemCount={selectedIds.length}
            />
        </>
    );
};

export default DataView;